import { Link } from "react-router-dom";
import { X, Crosshair, ExternalLink } from "lucide-react";
import { Timestamp } from "@/components/common";
import { isMoving, shipCategory } from "@/components/ShipLayer";
import { ShipEmissions, ShipParticulars } from "@/components/ShipEmissions";

const dms = (v, pos, neg) => {
  const a = Math.abs(v);
  const d = Math.floor(a);
  return `${String(d).padStart(pos === "N" ? 2 : 3, "0")}°${((a - d) * 60).toFixed(2).padStart(5, "0")}′${v >= 0 ? pos : neg}`;
};
const ago = (ts) => {
  const m = Math.round((Date.now() - new Date(ts).getTime()) / 60000);
  return m < 1 ? "just now" : m < 60 ? `${m} min ago` : `${Math.round(m / 60)} h ago`;
};
const statusLabel = (p) => (p.nav_status && p.nav_status !== "n/a" && p.nav_status !== "None" ? p.nav_status : isMoving(p) ? "Under way" : "Stopped");

function Stat({ label, value, testId }) {
  return <div className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2"><div className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{label}</div><div className="mt-0.5 text-lg font-semibold text-slate-100" data-testid={testId}>{value}</div></div>;
}

function Row({ label, value, hint }) {
  return <div className="flex justify-between gap-4 py-1.5 text-[13px]"><dt className="text-slate-500">{label}</dt><dd className="text-right text-slate-100">{value ?? "—"}{hint && <div className="text-[10px] text-slate-500">{hint}</div>}</dd></div>;
}

export function ShipDetail({ v, onClose, onZoom, children }) {
  const p = v.last_position;
  const cat = shipCategory(v.ship_type_code);
  const etaPast = v.eta && new Date(v.eta) < new Date();
  return (
    <div data-testid="ship-detail" className="rounded-xl border border-white/10 bg-[#0B1712]/95 p-4 shadow-2xl backdrop-blur-xl">
      <div className="flex items-start justify-between">
        <div className="flex flex-wrap gap-1.5">
          <span className="inline-flex items-center gap-1.5 rounded-md bg-white/5 px-2 py-0.5 text-xs text-slate-300"><i className="h-2 w-2 rounded-sm" style={{ background: cat.colour }} />{cat.label}</span>
          {v.flag && <span className="rounded-md bg-white/5 px-2 py-0.5 text-xs text-slate-300">{v.flag}</span>}
          {v.port && <span className="rounded-md bg-lime-500/10 px-2 py-0.5 text-xs text-lime-200">{v.terminal ? v.terminal.name : v.port.name}</span>}
        </div>
        <button data-testid="ship-detail-close" onClick={onClose} className="rounded p-1 text-slate-400 transition-colors hover:bg-white/10 hover:text-white"><X className="h-4 w-4" /></button>
      </div>
      <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-white" data-testid="ship-detail-name">{v.name || "Unnamed vessel"}</h2>
      <div className="mt-1 font-mono text-[11px] text-slate-400">MMSI {v.mmsi}{v.imo ? ` · IMO ${v.imo}` : ""}{v.call_sign ? ` · Call sign ${v.call_sign}` : ""}</div>
      <span className={`mt-3 inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs ${v.freshness.status === "OK" ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"}`} data-testid="ship-detail-status"><i className={`h-1.5 w-1.5 rounded-full ${v.freshness.status === "OK" ? "bg-emerald-400" : "bg-amber-400"}`} />{statusLabel(p)} · {ago(p.observed_at)}</span>
      <div className="mt-4 grid grid-cols-3 gap-2">
        <Stat label="Speed" value={<>{p.sog_knots ?? "—"}<span className="ml-0.5 text-xs text-slate-400">kn</span></>} testId="ship-detail-speed" />
        <Stat label="Course" value={p.cog_deg != null ? `${Math.round(p.cog_deg)}°` : "—"} testId="ship-detail-course" />
        <Stat label="Heading" value={p.heading_deg != null && p.heading_deg < 360 ? `${p.heading_deg}°` : "—"} testId="ship-detail-heading" />
      </div>
      <dl className="mt-3 divide-y divide-white/5">
        <Row label="Position" value={<span className="font-mono text-xs">{dms(p.lat, "N", "S")} {dms(p.lon, "E", "W")}</span>} />
        <Row label="Type" value={v.ship_type} />
        <Row label="Size" value={v.length_m ? `${v.length_m} × ${v.beam_m ?? "—"} m` : null} />
        <Row label="Draught" value={v.draught_m ? `${v.draught_m} m` : null} />
        <Row label="Destination" value={v.destination ? `“${v.destination}”` : null} hint={v.destination && "as entered on board; free text"} />
        <Row label="ETA" value={v.eta ? <Timestamp ts={v.eta} /> : null} hint={etaPast && "already passed; not updated by crew"} />
        <Row label="AIS class" value={v.ais_class} />
        <Row label="Last fix" value={<Timestamp ts={p.observed_at} />} />
      </dl>
      {children}
      <ShipParticulars v={v} />
      <ShipEmissions e={v.emissions} />
      <div className="mt-4 grid grid-cols-2 gap-2">
        <button data-testid="ship-detail-zoom" onClick={onZoom} className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-white/15 py-2 text-sm text-slate-200 transition-colors hover:bg-white/10"><Crosshair className="h-4 w-4" />Zoom</button>
        <Link data-testid="ship-detail-open" to={`/live/vessels/${v.mmsi}`} className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-lime-400/50 py-2 text-sm text-lime-200 transition-colors hover:bg-lime-400/10"><ExternalLink className="h-4 w-4" />Full history</Link>
      </div>
      <div className="mt-3 rounded-lg bg-white/[0.03] px-3 py-2 text-[11px] text-slate-500">Source: {v.source === "DATALASTIC" ? "Datalastic" : "AISTrail"} · Not for navigation · vessels are tracked, never accused</div>
    </div>
  );
}
